import api from 'api/axiosInstance';
import { createApiUrl, getToken } from '../util';
import { EditDSMPayload } from 'components/Tree/type';

type ShadingVersions = Omit<ShadingAnalysis, 'dsmEdits'>;

/** GET /edit-dsm/status/:uuid — the one status snapshot, for both edit kinds. */
export type ShadingStatusSnap = ShadingVersions & {
	inSync: boolean;
	runType: ShadingRunType;
};

/** POST /edit-dsm — save a DSM edit; no recompute. Always leaves the DSM dirty. */
export type EditDsmResponse = Omit<ShadingStatusSnap, 'shadingVersion'>;

/** POST /edit-dsm/recalculate-shading — 202 Accepted. Fired after EITHER edit kind. */
export type RecalcResponse = Pick<ShadingVersions, 'dsmVersion' | 'shadingStatus' | 'dsmDirty'> & {
	accepted: boolean;
	runType: ShadingRunType;
};

/** POST /edit-roof-segments, /translate-panels — save a facet edit; no recompute. */
export type FacetEditShadingStatus = Pick<ShadingVersions, 'dsmVersion' | 'shadingStatus'> & {
	inSync: false;
	runType: ShadingRunType;
};

/**
 * Read a FacetEditShadingStatus out of a response body that may carry it either way.
 */
export function normalizeFacetShadingStatus(body: unknown): FacetEditShadingStatus | undefined {
	if (!body || typeof body !== 'object') return undefined;

	const wrapped = (body as { shadingStatus?: unknown }).shadingStatus;
	const status = (wrapped && typeof wrapped === 'object' ? wrapped : body) as Partial<FacetEditShadingStatus>;

	return typeof status.dsmVersion === 'number' ? (status as FacetEditShadingStatus) : undefined;
}

/**
 * Pick the freshest facet-edit status out of a batch of settled facet mutations:
 * fulfilled + non-empty results only, keeping the highest dsmVersion seen.
 * Returns undefined when nothing usable settled.
 */
export function latestFacetShadingStatus(
	results: PromiseSettledResult<FacetEditShadingStatus | undefined>[],
): FacetEditShadingStatus | undefined {
	return results
		.filter((result): result is PromiseFulfilledResult<FacetEditShadingStatus> => result.status === 'fulfilled' && !!result.value)
		.map(result => result.value)
		.reduce<FacetEditShadingStatus | undefined>(
			(latest, res) => (!latest || (res.dsmVersion ?? 0) > (latest.dsmVersion ?? 0)) ? res : latest, undefined);
}

const authHeader = () => ({ Authorization: `Bearer ${getToken('Authorization')}` });

export async function saveDsmEdit(payload: EditDSMPayload): Promise<EditDsmResponse> {
	const { data } = await api.post(createApiUrl('/designs/edit-dsm'), payload, { headers: authHeader() });
	return data;
}

/**
 * "Done" — the single recompute trigger, fired after a TREE / FLATTEN edit AND after
 * a FACET edit (add / delete / ground-mount transform). The API picks the script from
 * `dsmDirty`: the full bake if there are unbaked DSM edits, otherwise the much
 * shorter facet rebuild. The 202 echoes back which one it will be as `runType`.
 */
export async function recalculateShading(uuid: string): Promise<RecalcResponse> {
	const { data } = await api.post(
		createApiUrl('/designs/edit-dsm/recalculate-shading'),
		{ uuid},
		{ headers: authHeader() },
	);
	return data;
}

/** The only progress source for a recompute + initial state (cross-instance safe — reads shared DB). */
export async function getShadingStatus(uuid: string): Promise<ShadingStatusSnap> {
	const { data } = await api.get(createApiUrl(`/designs/edit-dsm/status/${uuid}`), { headers: authHeader() });
	return data;
}
